import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Lottie from 'react-lottie-player'

import { Button, Flex, Modal, Typography } from '@percent/lemonade'
import { useLogger, useMutation, useQuery } from '@percent/workplace-giving/common/hooks'
import { BadgeAnimation } from './BadgeAnimation/BadgeAnimation'
import { getAccountAchievements } from '@percent/workplace-giving/api/account/achievements/achievements'
import confetti from '@percent/workplace-giving/assets/animations/confetti.json'
import styles from './BadgeEarnedPopup.module.scss'
import { markAchievementAsViewed } from '@percent/workplace-giving/api/gamification/markAchievementAsViewed/markAchievementAsViewed'
import { useBadgeEarnedPopup } from '@percent/workplace-giving/common/hooks/useBadgeEarnedPopup/useBadgeEarnedPopup'
import { useGamificationEnabled } from '@percent/workplace-giving/common/hooks/useGamificationEnabled/useGamificationEnabled'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'

export function BadgeEarnedPopup() {
  const { t } = useTranslation()
  const { logError } = useLogger()
  const { track } = useAnalytics()
  const gamificationEnabled = useGamificationEnabled()
  const [modalOpen, setModalOpen] = useState(false)
  const [animationActive, setAnimationActive] = useState(false)
  const [confettiAnimationPlaying, setConfettiAnimationPlaying] = useState(false)
  const { shouldRefetchAchievements, setShouldRefetchAchievements } = useBadgeEarnedPopup()

  const { data, status, refetch } = useQuery(['get'], getAccountAchievements, {
    enabled: gamificationEnabled
  })

  useEffect(() => {
    if (shouldRefetchAchievements) {
      refetch().then(() => {
        setShouldRefetchAchievements(false)
      })
    }
  }, [shouldRefetchAchievements, setShouldRefetchAchievements, refetch])

  const newlyEarnedAchievement = useMemo(() => {
    return data?.rulesAndAchievements.find(({ accountAchievement }) => {
      let seen = false

      if (accountAchievement?.id) {
        try {
          seen = Boolean(sessionStorage.getItem(accountAchievement?.id))
          // eslint-disable-next-line no-empty
        } catch {}
      }

      return accountAchievement && !accountAchievement.achievementViewedAt && !seen
    })
  }, [data?.rulesAndAchievements])

  const { mutateAsync } = useMutation({
    mutationFn: markAchievementAsViewed,
    onError: err => {
      logError(err)
    }
  })

  useEffect(() => {
    setModalOpen(!!newlyEarnedAchievement)
    setTimeout(() => {
      setAnimationActive(true)
    }, 800)

    setTimeout(() => {
      setConfettiAnimationPlaying(true)
    }, 1600)
  }, [newlyEarnedAchievement])

  const handleClose = () => {
    if (newlyEarnedAchievement?.accountAchievement?.id) {
      mutateAsync([{ achievementId: newlyEarnedAchievement.accountAchievement.id }])

      if (newlyEarnedAchievement?.accountAchievement?.id) {
        try {
          sessionStorage.setItem(newlyEarnedAchievement.accountAchievement.id, 'true')
          // eslint-disable-next-line no-empty
        } catch {}
      }

      track({
        event: 'Gamification Badge Earned Popup Closed',
        properties: {
          badgeName: newlyEarnedAchievement.achievement.name
        }
      })
    }
    setModalOpen(false)
  }

  if (!gamificationEnabled || status === 'pending' || status === 'error' || !newlyEarnedAchievement) {
    return null
  }

  return (
    <Modal open={modalOpen} width={360} borderRadiiSize="xlarge" onClose={handleClose} data-testid="badgeEarnedModal">
      <Flex direction="column" gap="2.5rem" justify="center" align="center" className={styles.Wrapper}>
        <Flex direction="column" gap="0.5rem" justify="center" align="center" ta="center">
          <Typography variant="h3">{t('workplace_giving.gamification.badgeEarnedModal.title')}</Typography>
          <Typography $color="gray600">{t('workplace_giving.gamification.badgeEarnedModal.descriptionNew')}</Typography>
        </Flex>
        <Flex direction="column" justify="center" align="center">
          {newlyEarnedAchievement ? (
            <>
              <BadgeAnimation badge={newlyEarnedAchievement.achievement} isActive={animationActive} />
              <Typography variant="h2">{newlyEarnedAchievement.achievement.name}</Typography>
            </>
          ) : null}
        </Flex>
        <Button onClick={handleClose} stretch data-testid="closeBadgeEarnedModalBtn" shape="oval">
          {t('workplace_giving.gamification.badgeEarnedModal.button')}
        </Button>
      </Flex>
      <Lottie
        data-testid="confettiAnimation"
        className={styles.Confetti}
        play={confettiAnimationPlaying}
        loop={false}
        animationData={confetti}
      />
    </Modal>
  )
}
